<template>
    <div>
        <div class="cv3">
            <!-- ============ MAIN PANEL (catalog) ============ -->
            <section class="cv3-main">
                <header class="cv3-head">
                    <div>
                        <Header :title="$t('checkout.v3.nomad_checkout')" customClass="cv3-title" />
                    </div>
                    <button type="button" class="cv3-open-sales" @click="openSalesDrawer = true">
                        <span>{{ $t('checkout.v3.open_sales') }}</span>
                        <span class="cv3-pill">{{ openSalesCount }}</span>
                    </button>
                </header>

                <CustomerBar :store="store" />

                <Catalog :store="store" />
            </section>

            <!-- ============ CART ASIDE ============ -->
            <CartPanel :store="store" @encaisser="openPayment" @new-sale="store.newSale()" @hold="onHold" />

            <!-- ============ PAYMENT MODAL ============ -->
            <PaymentDialog :store="store" :open="paymentOpen" @close="paymentOpen = false" @paid="onPaid" />

            <!-- ============ VENTES OUVERTES (open sales drawer) ============ -->
            <OpenSalesDrawer
                :store="store"
                :open="openSalesDrawer"
                @close="openSalesDrawer = false"
                @opened="openSalesDrawer = false"
            />
        </div>
    </div>
</template>

<script>
import { createCaisseStore } from './caisseStore'
import CustomerBar from './components/CustomerBar.vue'
import Catalog from './components/Catalog.vue'
import CartPanel from './components/CartPanel.vue'
import PaymentDialog from './components/PaymentDialog.vue'
import OpenSalesDrawer from './components/OpenSalesDrawer.vue'
import Header from '../../Shared/Header.vue'

export default {
    name: 'BoCaisseV3Index',
    components: { Header, CustomerBar, Catalog, CartPanel, PaymentDialog, OpenSalesDrawer },
    props: {
        token: { type: String, default: null },
        userId: { default: null },
        establishmentId: { default: 1 },
        stripeKey: { type: String, default: '' },
        categories: { type: [Object, Array], default: () => ({}) },
        customers: { type: Array, default: () => [] },
        currentCustomer: { default: null },
        staffs: { type: Array, default: () => [] },
        paymentMethods: { type: Array, default: () => [] },
        promoCodes: { type: Array, default: () => [] },
        routes: { type: Object, default: () => ({}) },
        canCreateOrder: { default: false },
        canModifyOrder: { default: false },
        canDeleteOrder: { default: false },
    },
    data() {
        return {
            store: createCaisseStore({
                token: this.token,
                userId: this.userId,
                establishmentId: this.establishmentId,
                stripeKey: this.stripeKey,
                categories: this.categories,
                customers: this.customers,
                currentCustomer: this.currentCustomer,
                staffs: this.staffs,
                paymentMethods: this.paymentMethods,
                promoCodes: this.promoCodes,
                routes: this.routes,
                canCreateOrder: this.canCreateOrder,
                canModifyOrder: this.canModifyOrder,
                canDeleteOrder: this.canDeleteOrder,
            }),
            paymentOpen: false,
            openSalesDrawer: false,
        }
    },
    computed: {
        openSalesCount() {
            return this.store.openSalesCount || 0
        },
    },
    watch: {
        // Keep the URL in sync with the active order: /back-office/v3/caisse?order=ID (reload-safe, shareable).
        'store.orderId'(id) {
            this.syncOrderUrl(id)
        },
    },
    async mounted() {
        try {
            // Reload-safe deep link: adopt ?order=ID from the URL before hydrating.
            const ord = new URLSearchParams(window.location.search).get('order')
            if (ord && /^\d+$/.test(ord)) this.store.orderId = parseInt(ord, 10)
            await this.store.refreshState()
            this.syncOrderUrl(this.store.orderId)
            this.store.fetchOpenSales()
        } catch (e) {
            this.$toast && this.$toast.error(this.$t('checkout.v3.load_error'))
        }
    },
    methods: {
        // Reflect the active order id in the address bar without reloading (replaceState — no history spam).
        syncOrderUrl(id) {
            try {
                const url = new URL(window.location.href)
                if (id) url.searchParams.set('order', id)
                else url.searchParams.delete('order')
                window.history.replaceState({}, '', url)
            } catch (e) {
                /* noop */
            }
        },
        // "Mettre en attente": the current order is already persisted (open) — it stays recoverable
        // from "Ventes ouvertes". We just reset to a fresh walk-in cart so the next sale starts blank
        // and never re-attaches to the parked order (the server resolves the customer's newest open order).
        async onHold() {
            if (!this.store.count || this.store.busy) return
            await this.store.newWalkIn()
            this.store.fetchOpenSales()
            this.$toast && this.$toast.success(this.$t('checkout.v3.sale_on_hold'))
        },
        openPayment() {
            if (!this.store.count) {
                this.$toast && this.$toast.warning(this.$t('checkout.v3.empty_cart'))
                return
            }
            this.paymentOpen = true
        },
        onPaid(payload) {
            this.paymentOpen = false
            const closeSale = payload && payload.closeSale
            if (closeSale) {
                this.$toast && this.$toast.success(this.$t('checkout.v3.sale_closed'))
                this.store.newSale()
            } else {
                // Acompte: the order stays open — refresh to show the recorded deposit / remaining.
                this.$toast && this.$toast.success(this.$t('checkout.v3.deposit_collected'))
                this.store.refreshState()
            }
            // Keep the "Ventes ouvertes" pill in sync (a clôture removes one; an acompte keeps it open).
            this.store.fetchOpenSales()
        },
    },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@600;700;800&display=swap');

.cv3 {
    /* ---- design tokens (inherited by all children via CSS custom properties) ---- */
    --cv3-bg: #060b26;
    --cv3-panel: #050f28;
    --cv3-aside: #1b194c;
    --cv3-selector: #202235;
    --cv3-search: #14162a;
    --cv3-input: #1f1a47;
    --cv3-tile: rgba(255, 255, 255, 0.03);
    --cv3-icon-tile: rgba(255, 255, 255, 0.1);
    --cv3-icon-tint: rgba(125, 45, 189, 0.24);
    --cv3-accent: #7d2dbd;
    --cv3-accent-2: #7d2dbd;
    --cv3-accent-3: #7d2dbd;
    --cv3-text: #fbfaff;
    --cv3-text-2: #c7c3e4;
    --cv3-muted: #8e89b5;
    --cv3-border: rgba(255, 255, 255, 0.08);
    --cv3-border-input: rgba(255, 255, 255, 0.3);
    --cv3-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
    --cv3-radius-panel: 19px;
    --cv3-radius-card: 15px;
    --cv3-radius-btn: 12px;

    font-family:
        'Poppins',
        -apple-system,
        Roboto,
        Helvetica,
        sans-serif;
    color: var(--cv3-text);
    display: flex;
    gap: 18px;
    align-items: stretch;
    height: calc(100vh - 96px);
    overflow: hidden;
    background: transparent;
    /* border-radius: var(--cv3-radius-panel); */
    padding: 0;

    * {
        box-sizing: border-box;
    }
    button {
        font-family: inherit;
        cursor: pointer;
    }
    button:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}

.cv3-main {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0; /* allow inner scroll instead of growing the page */
    overflow: hidden;
    background: var(--cv3-panel);
    border-radius: var(--cv3-radius-panel);
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.cv3-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.cv3-overline {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #fff;
    opacity: 0.85;
}
.cv3-title {
    margin: 2px 0 0;
    color: var(--cv3-text);
}
.cv3-open-sales {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 14px;
    border-radius: var(--cv3-radius-btn);
    border: 1px solid var(--cv3-border);
    background: #18143a;
    box-shadow: var(--cv3-shadow);
    color: var(--cv3-text);
    font-size: 14px;
    font-weight: 600;
}
.cv3-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 21px;
    height: 21px;
    padding: 0 7px;
    border-radius: 999px;
    background: var(--cv3-accent-3);
    color: #fff;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    font-size: 12px;
}

@media (max-width: 1100px) {
    .cv3 {
        flex-direction: column;
    }
}
</style>
